import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, EnvelopeSimple, ArrowLeft, Confetti } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * NeedleFound — the "Congratulations, you found the needle!" page.
 *
 * On mount:
 *   1. Posts to /api/contests/needle/enter (idempotent — a second visit
 *      by the same user just returns their existing entry).
 *   2. Displays the contest blurb, the finder's entry number, the running
 *      total entry count, and a first-finder callout for entry #1.
 *   3. Offers an opt-in toggle for email alerts about this contest.
 */
export default function NeedleFound() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [optedIn, setOptedIn] = useState(false);
  const [optBusy, setOptBusy] = useState(false);

  useEffect(() => {
    if (!user) return; // wait for auth
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/contests/needle/enter", {});
        if (cancelled) return;
        setState({ loading: false, error: null, data });
        setOptedIn(!!data?.entry?.opted_in);
      } catch (err) {
        if (cancelled) return;
        setState({ loading: false, error: formatErr(err), data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const toggleOptIn = async (next) => {
    if (optBusy) return;
    setOptBusy(true);
    try {
      await api.post("/contests/needle/opt-in", { opted_in: next });
      setOptedIn(next);
      toast.success(next ? "You'll get email alerts for this contest." : "Email alerts turned off.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setOptBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="needle-found-signin-gate">
        <Sparkle size={40} weight="duotone" className="mx-auto text-[#FFD700]" />
        <h1 className="mt-4 font-display font-black text-2xl">Sign in to claim your entry</h1>
        <p className="mt-2 text-sm text-zinc-400">
          You found the needle — sign in and we&apos;ll enter you into the giveaway.
        </p>
        <Link to="/login?next=/contests/needle-found" data-testid="needle-found-signin-btn" className="h57-btn-primary inline-flex mt-6">
          Sign in
        </Link>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest" data-testid="needle-found-loading">
        Recording your entry…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="needle-found-error">
        <h1 className="font-display font-bold text-xl text-red-400">Couldn&apos;t record your entry</h1>
        <p className="mt-2 text-sm text-zinc-400">{state.error}</p>
        <Link to="/browse" className="h57-btn-secondary inline-flex mt-6">
          <ArrowLeft size={14} weight="bold" /> Back to browse
        </Link>
      </div>
    );
  }

  const { already_entered, entry, entry_count_this_week, my_total_entries_this_contest, contest } = state.data;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="needle-found">
      <div className="text-center space-y-3">
        <Confetti size={56} weight="duotone" className="mx-auto text-[#FFD700]" />
        <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight" data-testid="needle-found-title">
          {already_entered
            ? "You\u2019ve already been entered this week — good luck!"
            : "Congratulations! You found the needle!"}
        </h1>
        <p className="text-sm text-zinc-400">
          {already_entered
            ? "Come back next week to hunt again — a new needle drops after Sunday."
            : `You've been entered for Week ${contest?.week_number || 1}.`}
        </p>
      </div>

      {/* Entry chips — this-week entry number, this-week total finders, and my total entries across the contest. */}
      <div className="grid grid-cols-3 gap-2">
        <div className="h57-surface p-4 text-center" data-testid="needle-found-entry-number">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Your Entry (Wk {contest?.week_number || 1})</div>
          <div className="mt-1 font-display font-black text-2xl text-[#FFD700]">#{entry.entry_number}</div>
        </div>
        <div className="h57-surface p-4 text-center" data-testid="needle-found-total-entries">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Finders This Week</div>
          <div className="mt-1 font-display font-black text-2xl text-emerald-400">{entry_count_this_week}</div>
        </div>
        <div className="h57-surface p-4 text-center" data-testid="needle-found-my-contest-total">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Your Contest Total</div>
          <div className="mt-1 font-display font-black text-2xl text-white">
            {my_total_entries_this_contest ?? 1}
          </div>
        </div>
      </div>

      {/* First-finder bragging */}
      {entry.entry_number === 1 && !already_entered && (
        <div className="h57-surface p-4 border border-[#FFD700]/40 bg-[#FFD700]/5 text-center" data-testid="needle-found-first-finder">
          <div className="font-display font-black text-sm text-[#FFD700] uppercase tracking-widest">🏆 First to find it</div>
          <p className="mt-1 text-xs text-zinc-400">Legend. Nobody found this needle before you.</p>
        </div>
      )}

      {/* Contest blurb */}
      {contest?.blurb && (
        <div className="h57-surface p-5" data-testid="needle-found-blurb">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">
            → {contest.name || "Current Contest"}
          </div>
          <p className="mt-2 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
            {contest.blurb}
          </p>
          <Link to="/contests/terms" className="mt-3 inline-block text-[11px] text-zinc-500 hover:text-[#FFD700] underline" data-testid="needle-found-terms-link">
            → Contest Details &amp; Terms
          </Link>
        </div>
      )}

      {/* Opt-in for alerts */}
      <div className="h57-surface p-5 space-y-3" data-testid="needle-found-optin">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={16} weight="bold" className="text-[#FFD700]" />
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Alerts for this contest</h3>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={optedIn}
            onChange={(e) => toggleOptIn(e.target.checked)}
            disabled={optBusy}
            data-testid="needle-found-optin-checkbox"
            className="mt-1 accent-[#FFD700] shrink-0"
          />
          <span className="text-sm text-zinc-300 leading-snug">
            Email me when new needles drop and when the drawing happens.{" "}
            <span className="text-zinc-500 text-xs">
              (Only for this contest — one email at each event, no spam.)
            </span>
          </span>
        </label>
      </div>

      <div className="flex gap-3 justify-center pt-2">
        <Link to="/browse" data-testid="needle-found-back" className="h57-btn-secondary inline-flex items-center gap-2">
          <ArrowLeft size={14} weight="bold" /> Back to Browse
        </Link>
      </div>
    </div>
  );
}
